import { Box, Paper, Stack, Typography } from "@mui/material";
import {
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairlineTop,
  HEADING_SIZE,
} from "@/src/components/common/broadsheet/tokens";
import Panel from "@/src/components/common/broadsheet/panel";

export const FORM_COLUMN_WIDTH = "85%";

export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: FONT_TEXT,
    fontSize: "0.9375rem",
    borderRadius: 0,
    backgroundColor: "custom.newsprintRaised",
    "& fieldset": {
      borderColor: "custom.ruleHairline",
      borderBottomWidth: "2px",
      borderBottomColor: "primary.main",
    },
    "&:hover fieldset, &.Mui-focused fieldset": {
      borderColor: "primary.main",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: FONT_UI,
    fontSize: "0.8125rem",
    fontWeight: 600,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: FONT_UI,
    fontSize: "0.71875rem",
    marginLeft: 0,
  },
};

export const mergeFieldSx = (sx?: Record<string, any>) =>
  sx
    ? {
        ...fieldSx,
        ...sx,
        "& .MuiOutlinedInput-root": {
          ...fieldSx["& .MuiOutlinedInput-root"],
          ...(sx["& .MuiOutlinedInput-root"] ?? {}),
        },
      }
    : fieldSx;

export const formSurfaceSx = {
  px: 0,
  py: { xs: 1.25, md: 1.5 },
  border: 0,
  borderRadius: 0,
  backgroundColor: "transparent",
  boxShadow: "none",
  ...hairlineTop,
};

export const captchaFieldSx = {
  "& .MuiOutlinedInput-root": { paddingRight: 0 },
  "& .MuiInputAdornment-positionEnd": {
    height: "auto",
    maxHeight: "none",
    alignSelf: "stretch",
    marginLeft: 0,
  },
};

export const captchaFrameSx = {
  display: "flex",
  alignItems: "stretch",
  alignSelf: "stretch",
  borderRadius: 0,
  overflow: "hidden",
  borderLeft: "1px solid",
  borderColor: "custom.ruleHairline",
};

export const captchaImageStyle: React.CSSProperties = {
  maxWidth: "150px",
  height: "100%",
  objectFit: "cover",
  display: "block",
  background: "#fff",
};

export const captchaButtonSx = {
  px: 0.75,
  minWidth: 0,
  borderRadius: 0,
  boxShadow: "none",
  fontFamily: FONT_UI,
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
};

export const requiredLabel = (label: string, isRequired?: boolean) => (
  <>
    {label}
    {isRequired && (
      <Typography component="span" sx={{ ml: 0.5, color: "custom.required" }}>
        *
      </Typography>
    )}
  </>
);

export const StandingNote = ({ children }: { children: React.ReactNode }) => (
  <Panel
    sx={{
      fontFamily: FONT_TEXT,
      fontSize: "0.9375rem",
      lineHeight: 1.6,
      color: "custom.inkMuted",
    }}
  >
    {children}
  </Panel>
);

export const FormSection = ({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <Paper elevation={0} sx={formSurfaceSx}>
    <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", mb: 1 }}>
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: "1.5rem",
          fontFamily: FONT_DISPLAY,
          fontWeight: 700,
          fontSize: HEADING_SIZE.section,
          lineHeight: 1,
          color: "custom.kicker",
        }}
      >
        {step}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: HEADING_SIZE.section,
            lineHeight: 1.2,
            color: "custom.ink",
          }}
        >
          {title}
        </Typography>
        {description ? (
          <Typography
            component="p"
            sx={{
              m: 0,
              mt: 0.125,
              fontFamily: FONT_UI,
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              color: "custom.inkMuted",
            }}
          >
            {description}
          </Typography>
        ) : null}
      </Box>
    </Stack>
    <Box sx={{ maxWidth: { xs: "100%", md: FORM_COLUMN_WIDTH }, mx: "auto" }}>
      {children}
    </Box>
  </Paper>
);

export const FormMasthead = ({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      justifyContent: "space-between",
      columnGap: 1,
      rowGap: 0.375,
    }}
  >
    <Typography
      component="h1"
      sx={{
        m: 0,
        fontFamily: FONT_DISPLAY,
        fontWeight: 700,
        fontSize: HEADING_SIZE.page,
        lineHeight: 1.05,
        letterSpacing: "0.01em",
        color: "custom.ink",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);
